import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import { connect } from '@reverbdotcom/commons/src/connect';
import { RCAlertBox, buttonClassName } from '@reverbdotcom/cadence/components';

import SignupSigninModal from '../components/signup_signin_modal';
import { IUser } from '../components/user_context_provider';
import { useExpEnabled, useUser } from '../user_hooks';
import { isOwner } from '../user_context_helpers';
import CreateOfferModal from './create_offer_modal';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { LISTING_STATES } from '../constants';
import { mParticleListingFields } from '../components/with_mparticle_listings';
import { withRouter, WithRouterProps } from 'react-router';
import { resolveCurrentView } from '../event_tracking/favorites';
import { withCreateUserActionMutation, CoreUserActionsCreateMutationFn } from '../user_actions/create_action_mutation';
import { SellerOpenToOffersPopover } from './seller_open_to_offers_popover';
import {
  core_apimessages_BumpKey,
  core_apimessages_Listing_ListingType,
  ListingCreateOfferButtonData,
  CreateUserActionMeData,
  Item2AvailableActionsData,
  core_apimessages_UserActionName,
  core_apimessages_UserActionStatus,
} from '../gql/graphql';
import { isItemPage } from '../url_helpers';

interface IExternalProps {
  listing: ListingCreateOfferButtonData.Fragment;
  isSeller?: boolean;
  disabled?: boolean;
  offerCount?: number;
  position?: number;
  parentComponentName?: string;
  bumpKey?: core_apimessages_BumpKey;
  userTotalOffersCount?: number;
  className?: string;
  availableActions?: Item2AvailableActionsData.Fragment[];
  me?: CreateUserActionMeData.Fragment;
}

interface MutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type IProps = IExternalProps & MutationProps & WithRouterProps;

export const ListingCreateOfferButtonFragment = gql`
  fragment ListingCreateOfferButtonData on Listing {
    id
    _id
    state
    listingType
    sellerId
    ...mParticleListingFields
  }
  ${mParticleListingFields}
`;

export const Item2LoggedInOffersCountFragment = gql`
  fragment Item2LoggedInOffersCountData on rql_Me {
    id
    profile {
      totalBuyerOffersCreated
    }
  }
`;

function getTrackedExperiments(user: Partial<IUser>, userTotalOffersCount: number) {
  if (!!user && !user.loggedOut) {
    if (userTotalOffersCount === 0) {
      return [
        experiments.OPEN_TO_OFFERS_LOGGED_IN,
      ];
    } else {
      return [];
    }
  }

  return [];
}

export function canAcceptOffers(listing: ListingCreateOfferButtonData.Fragment) {
  return listing.state === LISTING_STATES.LIVE &&
    listing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER;
}

export function availableOfferButtonAction(actions: Item2AvailableActionsData.Fragment[]) {
  return actions?.find(action => {
    return action.name === core_apimessages_UserActionName.SELLER_OPEN_TO_OFFERS_CALLOUT
      && action.status === core_apimessages_UserActionStatus.AVAILABLE;
  });
}

function Button({ disabled, toggleModal, applyLoudSecondaryCTA }) {
  return (
    <button
      className={applyLoudSecondaryCTA ? classNames(buttonClassName({ theme: 'loud', variant: 'muted' }), 'offer-action') : 'offer-action'}
      data-disabled={disabled || null}
      onClick={toggleModal}
      type="button"
    >
      {I18n.t('commons.offers.action.create')}
    </button>
  );
}

function OfferSignupCallout() {
  return (
    <div className="mb-4">
      <RCAlertBox type="success">
        <h4 className="size-110 weight-bold mb-2">
          {I18n.t('commons.offers.benefits.title')}
        </h4>
        <ul className="fa-ul">
          <li>
            <span className="fa fa-check fa-li color-green" />
            {I18n.t('commons.offers.benefits.saveMoney')}
          </li>
          <li>
            <span className="fa fa-check fa-li color-green" />
            {I18n.t('commons.offers.benefits.quickResponse')}
          </li>
        </ul>
      </RCAlertBox>
    </div>
  );
}

function SignupSigninModalOrOfferModal({ user, openDialog, handleClose, listing, isSeller, bumpKey }) {
  if (!user || user.loggedOut) {
    return (
      <SignupSigninModal
        active={openDialog}
        onRequestClose={handleClose}
        trackSource="Offer"
        messageComponent={<OfferSignupCallout />}
      />
    );
  }

  return (
    <CreateOfferModal
      listingId={listing.id}
      isSeller={isSeller}
      onCancel={handleClose}
      isDisplayed={openDialog}
      bumpKey={bumpKey}
    />
  );
}

export function CreateOfferButton(props: IProps) {
  const user = useUser();
  const [openDialog, setOpenDialog] = React.useState(false);
  const applyLoudSecondaryCTA = useExpEnabled(experiments.ORANGE_SECONDARY_CTAS_ON_ITEM_PAGE) && isItemPage(props.location);

  React.useEffect(() => {
    if (openDialog) {
      const trackedExperiments = getTrackedExperiments(user, props.userTotalOffersCount);

      trackEvent({
        eventName: MParticleEventName.ClickedOfferButton,
        listingId: props.listing.id,
        listing: props.listing,
        position: props.position,
        componentName: props.parentComponentName,
        experiments: formatExperimentsForEventAttributes(user, trackedExperiments),
        currentView: resolveCurrentView(props.location),
        role: props.isSeller ? 'seller' : 'buyer',
      });
    }
  }, [openDialog]);

  function toggleModal(e) {
    // When handle closing the modal, it will blow up because there is no event being passed in
    // so we have to guard
    e.preventDefault();
    e.stopPropagation();
    setOpenDialog(!openDialog);
  }

  if (!canAcceptOffers(props.listing)) {
    return null;
  }

  return (
    <div className={props.className}>
      <SellerOpenToOffersPopover
        availableActions={props.availableActions}
        createUserAction={props.createUserAction}
        me={props.me}
        listing={props.listing}
        currentView={resolveCurrentView(props.location)}
        anchor={(
          <div>
            <Button
              disabled={props.disabled || isOwner(user, { sellerId: props.listing.sellerId })}
              toggleModal={toggleModal}
              applyLoudSecondaryCTA={applyLoudSecondaryCTA}
            />
          </div>
        )}
      />
      {!!props.offerCount &&
        <div
          data-offer-count
          className="size-80 opacity-70 mt-1 align-center"
        >
          {I18n.t('commons.offers.offerCount', { count: props.offerCount })}
        </div>
      }
      <SignupSigninModalOrOfferModal
        user={user}
        openDialog={openDialog}
        handleClose={() => { setOpenDialog(false); }}
        listing={props.listing}
        isSeller={props.isSeller}
        bumpKey={props.bumpKey}
      />
    </div>
  );
}

export default connect<IExternalProps>([
  withCreateUserActionMutation,
  withRouter,
])(CreateOfferButton);
